import {
  BoundDateField,
  BoundSelectField,
  BoundTextField,
  Button,
  FormLines,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useModal,
  useSnackbar,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { useCallback } from "react";
import { createProjectUrl } from "src/RouteUrls";
import { CreateProjectFromReadyPlanInput, InputMaybe, LotType } from "src/generated/graphqlTypes";
import {
  EditProductOfferingConfigQuery,
  useCreateProjectFromConfigMetaQuery,
  useCreateProjectFromProductOfferingConfigMutation,
} from "src/routes/libraries/product-offering-configs/AddProductOfferingConfigPage.generated";
import { fail, queryResult } from "src/utils";
import { DateOnly } from "src/utils/dates";
import { openNewTab } from "src/utils/window";

type CreateProjectFromOfferingConfigModalProps = {
  developmentId: string | null | undefined;
  projectId: string | null | undefined;
  productOfferingConfig: Pick<EditProductOfferingConfigQuery["productOfferingConfig"], "id" | "name">;
};

export function CreateProjectFromOfferingConfigModal(props: CreateProjectFromOfferingConfigModalProps) {
  const { closeModal } = useModal();
  const { developmentId, projectId, productOfferingConfig } = props;
  const { name } = productOfferingConfig;
  const query = useCreateProjectFromConfigMetaQuery({
    variables: {
      developmentId: developmentId ?? "",
      projectId: projectId ?? "",
      productOfferingConfigId: productOfferingConfig.id,
      skipDevelopment: !developmentId,
    },
  });
  const { triggerNotice } = useSnackbar();
  const [createProject] = useCreateProjectFromProductOfferingConfigMutation({
    onCompleted: ({ createProjectFromReadyPlan }) => {
      const { project } = createProjectFromReadyPlan;
      openNewTab(createProjectUrl(project.id));
      closeModal();
      triggerNotice({ message: `New project ${project.name} created.` });
    },
  });
  const formState = useFormState({
    config: createProjectFormConfig,
    init: {
      query,
      map: (input) => ({
        rpId: input.productOfferingConfig.productOffering.id ?? "",
        rpoIds: input.productOfferingConfig.readyPlanOptions.map((rpo) => rpo.id),
        street1: `New Project From POC "${name}"`,
        cohortId: input.cohorts?.first?.id,
        lotType: LotType.Bool,
        startDate: new Date(),
        buildType: "New Build",
      }),
    },
    addRules(state) {
      if (projectId) {
        state.parentId.rules.push(required);
      }
    },
  });

  const onSave = useCallback(async () => {
    const input = mapFormToInput(formState);
    await createProject({ variables: { input } });
  }, [formState, createProject]);

  const body = queryResult(query, (data) => {
    return (
      <FormLines>
        <BoundTextField field={formState.street1} label="Project Name" />
        <BoundDateField field={formState.startDate} label="Start Date" />
        {projectId && (
          <>
            <BoundSelectField
              field={formState.parentId}
              label="Subdivision"
              placeholder="Select Subdivision"
              options={data.project!.children}
            />
            <BoundSelectField
              field={formState.primaryProjectId}
              label="Primary Project"
              placeholder="Select Primary Project"
              options={query.data?.projectsPage.entities ?? []}
            />
          </>
        )}
      </FormLines>
    );
  });
  return (
    <Observer>
      {() => (
        <>
          <ModalHeader>Create Project from Offering Configuration</ModalHeader>
          <ModalBody>{body}</ModalBody>
          <ModalFooter>
            <Button variant="quaternary" size="md" label="Cancel" onClick={closeModal} />
            <Button variant="primary" size="md" label="Create" disabled={!formState.valid} onClick={onSave} />
          </ModalFooter>
        </>
      )}
    </Observer>
  );
}

type FormConfig = Omit<
  CreateProjectFromReadyPlanInput,
  "buildAddress" | "cohortId" | "lotType" | "siteSpecificCosts" | "lotPartitionId" | "buildType" | "startDate"
> & {
  street1: InputMaybe<string>;
  startDate: InputMaybe<Date>;
};

const createProjectFormConfig: ObjectConfig<FormConfig> = {
  rpId: { type: "value", rules: [required] },
  rpoIds: { type: "value", rules: [required] },
  street1: { type: "value", rules: [required] },
  startDate: { type: "value", rules: [required] },
  parentId: { type: "value" },
  primaryProjectId: { type: "value" },
};

function mapFormToInput(form: ObjectState<FormConfig>): CreateProjectFromReadyPlanInput {
  if (!form.valid) fail("Form is not valid");
  return {
    buildAddress: {
      street1: form.street1.value,
      city: "Test City",
      state: "TX",
      postalCode: "12345",
      country: "US",
    },
    startDate: new DateOnly(form.startDate.value!),
    siteSpecificCosts: [],
    buildType: "New Build",
    rpId: form.rpId.value,
    rpoIds: form.rpoIds.value,
    enableProductConfigPlan: true,
    isTest: true,
    parentId: form.parentId.value,
    primaryProjectId: form.primaryProjectId.value,
  };
}
